import type { Applicant, AuthState } from "./types";

const STORAGE_KEY = "hallticket.auth.state.v1";
const ID_TOKEN_STORAGE_KEY = "id_token";
/** Survives new tabs; sessionStorage does not. Used to align Firebase persistence after explicit sign-out. */
const EXPLICIT_SIGNOUT_KEY = "hallticket.auth.explicitSignOut.v1";

const listeners = new Set<() => void>();

const defaultState: AuthState = {
  status: "signed_out",
  firebaseIdToken: null,
  applicant: null,
};

let state: AuthState = loadState();

function canUseSessionStorage(): boolean {
  return typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";
}

function canUseLocalStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function persistIdToken(token: string | null): void {
  if (!canUseLocalStorage()) {
    return;
  }

  if (token) {
    window.localStorage.setItem(ID_TOKEN_STORAGE_KEY, token);
    return;
  }

  window.localStorage.removeItem(ID_TOKEN_STORAGE_KEY);
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isAuthState(value: unknown): value is AuthState {
  if (!isObject(value)) {
    return false;
  }

  const status = value.status;
  const firebaseIdToken = value.firebaseIdToken;
  const applicant = value.applicant;

  const isStatusValid = status === "signed_out" || status === "needs_onboarding" || status === "signed_in";

  const isTokenValid = typeof firebaseIdToken === "string" || firebaseIdToken === null;
  const isApplicantValid = applicant === null || isObject(applicant);

  return isStatusValid && isTokenValid && isApplicantValid;
}

function loadState(): AuthState {
  if (!canUseSessionStorage()) {
    return defaultState;
  }

  const raw = window.sessionStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return defaultState;
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    return isAuthState(parsed) ? parsed : defaultState;
  } catch {
    return defaultState;
  }
}

function persistState(nextState: AuthState): void {
  if (!canUseSessionStorage()) {
    return;
  }

  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(nextState));
}

function emit(): void {
  listeners.forEach((listener) => {
    listener();
  });
}

function isSameAuthState(currentState: AuthState, nextState: AuthState): boolean {
  return (
    currentState.status === nextState.status &&
    currentState.firebaseIdToken === nextState.firebaseIdToken &&
    currentState.applicant === nextState.applicant
  );
}

export function getAuthState(): AuthState {
  return state;
}

export function subscribeAuthState(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setAuthState(nextState: AuthState): void { 
  if (isSameAuthState(state, nextState)) {
    return;
  }

  state = nextState;
  persistState(nextState);
  persistIdToken(nextState.firebaseIdToken);
  emit();
}

function markExplicitSignOutForNextLoad(): void {
  if (!canUseLocalStorage()) {
    return;
  }
  window.localStorage.setItem(EXPLICIT_SIGNOUT_KEY, "1");
}

export function clearExplicitSignOutRecord(): void {
  if (!canUseLocalStorage()) {
    return;
  }
  window.localStorage.removeItem(EXPLICIT_SIGNOUT_KEY);
}

export function hasExplicitSignOutMark(): boolean {
  if (!canUseLocalStorage()) {
    return false;
  }
  return window.localStorage.getItem(EXPLICIT_SIGNOUT_KEY) === "1";
}

/** Call from `signOutUser` after clearing app auth so a later page load can drop a stale Firebase session. */
export function recordExplicitUserSignOut(): void {
  markExplicitSignOutForNextLoad();
}

export function clearAuthState(): void {
  setAuthState(defaultState);
}

export function setNeedsOnboarding(firebaseIdToken: string): void {
  clearExplicitSignOutRecord();
  setAuthState({
    status: "needs_onboarding",
    firebaseIdToken,
    applicant: null,
  });
}

export function setSignedIn(firebaseIdToken: string, applicant: Applicant): void {
  clearExplicitSignOutRecord();
  setAuthState({
    status: "signed_in",
    firebaseIdToken,
    applicant,
  });
}
